import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Home, Info, Image as ImageIcon, Phone, ShoppingCart } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';

export interface MobileMenuSection {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** When set, renders as Link instead of scroll target */
  href?: string;
}

interface MobileMenuProps {
  sections?: MobileMenuSection[];
  restaurantName?: string;
  phone?: string;
}

/**
 * Hamburger menu for mobile navigation.
 * Slides in from the right with smooth animation.
 * Uses React Portal to render at document root to avoid z-index/overflow issues.
 */
const MobileMenu: React.FC<MobileMenuProps> = ({ sections = [], restaurantName, phone }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Ensure we're mounted before using portal
  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const defaultSections = [
    { id: 'top', label: t('public.menu'), icon: Home },
    { id: 'about', label: t('public.about'), icon: Info },
    { id: 'gallery', label: t('public.gallery'), icon: ImageIcon },
  ];

  const menuSections = sections.length > 0 ? sections : defaultSections;

  const handleScrollClick = (sectionId: string) => {
    setIsOpen(false);
    setTimeout(() => {
      if (sectionId === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  // Close sidebar when pressing Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const menuContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Overlay - Full screen with proper z-index */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            style={{ zIndex: 9998 }}
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Slide-in Menu */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 w-[85%] max-w-[320px] bg-background border-l border-border shadow-2xl flex flex-col"
            style={{ zIndex: 9999 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-card/50 shrink-0">
              <div className="flex flex-col min-w-0 flex-1 pr-2">
                <span className="font-semibold text-base">{t('public.menu')}</span>
                {restaurantName && (
                  <span className="text-xs text-muted-foreground truncate">{restaurantName}</span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-10 w-10 p-0 shrink-0"
                aria-label={t('common.closeMenu')}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-2">
              {menuSections.map((section) => {
                const Icon = section.icon;
                const baseClass = "w-full text-left px-4 py-3.5 rounded-lg hover:bg-muted active:bg-muted/80 transition-colors text-foreground font-medium flex items-center gap-3 touch-manipulation";
                if (section.href) {
                  return (
                    <Link
                      key={section.id}
                      to={section.href}
                      onClick={() => setIsOpen(false)}
                      className={baseClass}
                    >
                      {Icon && <Icon className="w-5 h-5 text-muted-foreground shrink-0" />}
                      <span className="truncate">{section.label}</span>
                    </Link>
                  );
                }
                return (
                  <button
                    key={section.id}
                    onClick={() => handleScrollClick(section.id)}
                    className={baseClass}
                  >
                    {Icon && <Icon className="w-5 h-5 text-muted-foreground shrink-0" />}
                    <span className="truncate">{section.label}</span>
                  </button>
                );
              })}
              
              {/* Call button if phone is provided */}
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="w-full text-left px-4 py-3.5 rounded-lg hover:bg-muted active:bg-muted/80 transition-colors text-foreground font-medium flex items-center gap-3 touch-manipulation block"
                >
                  <Phone className="w-5 h-5 text-muted-foreground shrink-0" />
                  <span className="truncate">{t('landing.call')}</span>
                </a>
              )}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-border bg-card/30 backdrop-blur-sm shrink-0">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground truncate">{t('app.language')}</span>
                <LanguageSwitcher />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* Hamburger Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="h-10 w-10 p-0 hover:bg-muted active:bg-muted/80"
        aria-label={t('common.openMenu')}
      >
        <Menu className="w-6 h-6" />
      </Button>

      {/* Portal the menu to document.body to avoid z-index/overflow issues */}
      {mounted && createPortal(menuContent, document.body)}
    </>
  );
};

export default MobileMenu;
